import React, { createContext, useContext, useState, useEffect } from "react";
import { DEFAULT_NOTIFS, NAV_ACCESS, ROLE_HOME, ROLE_LABELS } from "../data/mockData";
import { getEvents, getMe, upsertSalesReview, upsertMarketingReview } from "../services/api";

export const REJECT_REASONS = [
  {code:"not_relevant", label:"Not relevant for this event",  signal:"Event Fit"},
  {code:"low_readiness",label:"Export readiness too low",     signal:"Export Readiness"},
  {code:"price_high",   label:"Pricing / package too high",   signal:"Commercial"},
  {code:"wrong_cat",    label:"Wrong product category",       signal:"Category"},
  {code:"already_cust", label:"Already a customer / in CRM",  signal:"Dedup"},
  {code:"too_small",    label:"Too small / not a target",     signal:"Priority"},
  {code:"other",        label:"Other",                        signal:"—"},
];

// ─── Context ──────────────────────────────────────────────────────────────────
const AppContext = createContext(null);

export function AppProvider({ children }) {
  // ── Navigation ──────────────────────────────────────────────────────────────
  const [view, setView] = useState("landing");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // ── Role & identity ─────────────────────────────────────────────────────────
  // currentRole is seeded from the signed-in user's role (users table) via
  // GET /users/me — not a manual default. It stays until resolution completes.
  const [currentRole, setCurrentRole] = useState("Sales Head");
  const [currentUser, setCurrentUser] = useState("");   // email — used for created_by/decided_by
  const [currentUserId, setCurrentUserId] = useState(null); // users.user_id (FK)
  const [authResolved, setAuthResolved] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    getMe()
      .then(u => {
        const label = ROLE_LABELS[u.role];
        if (!label) { setAccessDenied(true); return; }   // seeded but unknown role slug
        setCurrentUser(u.email || u.name || "");
        setCurrentUserId(u.user_id || null);
        setCurrentRole(label);
      })
      .catch(() => setAccessDenied(true))                 // 401/403 — not a seeded user
      .finally(() => setAuthResolved(true));
  }, []);

  // ── Core data ───────────────────────────────────────────────────────────────
  const [events, setEvents]           = useState([]);
  const [companies, setCompanies]     = useState([]);
  const [trackedShows, setTrackedShows] = useState([]);

  useEffect(() => {
    getEvents().then(setEvents).catch(() => {});
  }, []);

  // ── Notifications ───────────────────────────────────────────────────────────
  const [notifs, setNotifs] = useState(DEFAULT_NOTIFS);
  const [notifOpen, setNotifOpen] = useState(false);

  // ── Toast ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // ── Save-in-progress indicator (count of pending API saves) ──────────────
  const [savingCount, setSavingCount] = useState(0);

  // ── Review companies cache (keyed by show_id; persists across page transitions) ──
  const [reviewCompanies, setReviewCompanies] = useState({});

  // ── Year to pre-select when navigating to Company Master ─────────────────────
  const [companiesYear, setCompaniesYear] = useState(null);

  // ── Company Master UI state (filters / search / sort / view) — persisted here
  // so it survives navigating into a company detail and back (the page unmounts).
  const [companiesUi, setCompaniesUi] = useState(null);

  // ── Lead review context (passed from LeadsPage to CompanyDetailPage) ─────────
  const [leadReviewCtx, setLeadReviewCtx] = useState(null);

  // ── Pending leads navigation (set before nav("leads") to pre-select a show) ──
  const [pendingLeadsShow, setPendingLeadsShow] = useState(null); // { show_id, tab }

  // ── Pending insights navigation (set before nav("insights") to pre-apply filters) ──
  const [pendingInsights, setPendingInsights] = useState(null); // { ite_event_id, show_id, year }

  // ── Decision modal (shared across pages — drives the single-company Reject / Research / Refer prompts) ──
  const [decisionModal, setDecisionModal] = useState(null);
  // shape: { asgnId, coId, coName, status, kind: "reject"|"research"|"refer", showName }

  const _toSnake = (s) => s.toLowerCase().replace(/ /g, "_").replace(/-/g, "_");

  const UNDECIDED_STATUSES = ["Pending", "Opened"];
  const updateReviewStatus = (asgnId, coId, status, extra = {}, silent = false) => {
    const today = new Date().toISOString().slice(0, 10);
    const isUndecided = UNDECIDED_STATUSES.includes(status);
    // Who decided is the signed-in user, resolved from users.user_id by GET /users/me.
    // This used to be a hardcoded seed GUID per role, which attributed every
    // reviewer's decision to a placeholder account that exists in no environment.
    const decidedBy = isUndecided ? null : currentUserId;
    const existingNote = (reviewCompanies[asgnId] || []).find(c => c.id === coId)?.note ?? null;
    setReviewCompanies(prev => ({
      ...prev,
      [asgnId]: (prev[asgnId] || []).map(c =>
        c.id === coId ? {
          ...c,
          reviewStatus: status,
          decidedAt: isUndecided ? null : today,
          note: "decisionNote" in extra ? (extra.decisionNote || "") : c.note,
        } : c
      ),
    }));
    const prevStatus = (reviewCompanies[asgnId] || []).find(c => c.id === coId)?.reviewStatus || "Pending";
    const body = {
      show_id: asgnId, company_id: coId,
      review_status: _toSnake(status), decided_by: decidedBy,
      reject_reason: extra.rejectReason || null,
      reject_signal: extra.rejectSignal || null,
      notes: "decisionNote" in extra ? (extra.decisionNote || null) : existingNote,
    };
    const save = currentRole === "Marketing Manager"
      ? upsertMarketingReview(body)
      : upsertSalesReview(body);
    setSavingCount(n => n + 1);
    save.then(() => { if (!silent) showToast("Saved", "success"); })
        .catch(() => {
          setReviewCompanies(prev => ({
            ...prev,
            [asgnId]: (prev[asgnId] || []).map(c =>
              c.id === coId ? { ...c, reviewStatus: prevStatus } : c
            ),
          }));
          showToast("Save failed — please retry", "error");
        })
        .finally(() => setSavingCount(n => n - 1));
  };

  const requestDecision = (asgnId, coId, status, coName, showName) => {
    if (["Rejected", "Mark Not Met"].includes(status)) {
      setDecisionModal({ asgnId, coId, status, coName, showName, kind: "reject" });
      return;
    }
    if (status === "Needs Research") {
      setDecisionModal({ asgnId, coId, status, coName, showName, kind: "research" });
      return;
    }
    if (status === "Refer to Another Event") {
      setDecisionModal({ asgnId, coId, status, coName, showName, kind: "refer" });
      return;
    }
    updateReviewStatus(asgnId, coId, status);
  };

  // ── Campaign modal (shared across pages) ────────────────────────────────────
  const [campaignModal, setCampaignModal] = useState(null);

  // ── Role change: redirect if current view not allowed ───────────────────────
  useEffect(() => {
    const allowed = NAV_ACCESS[currentRole] || [];
    setView(v => {
      if (v === "landing") return v;   // landing is a valid entry screen for any role
      if (!allowed.includes(v)) return ROLE_HOME[currentRole] || "portfolio";
      if (currentRole === "Sales Head" && v === "leads") return "pipeline";
      return v;
    });
  }, [currentRole]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const nav = (v) => setView(v);

  const navToCompany = (id) => {
    setSelectedCompanyId(id);
    setView("companyDetail");
  };

  const changeEvent = () => {
    setSelectedEvent(null);
    setView("portfolio");
  };

  const updateCo = (id, field, val) =>
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, [field]: val } : c));

  return (
    <AppContext.Provider value={{
      // navigation
      view, setView, nav,
      selectedEvent, setSelectedEvent, changeEvent,
      selectedCompanyId, setSelectedCompanyId, navToCompany,
      // role & identity
      currentRole, setCurrentRole,
      currentUser, currentUserId,
      authResolved, accessDenied,
      // data
      events,
      companies, setCompanies, updateCo,
      trackedShows, setTrackedShows,
      // review companies cache
      reviewCompanies, setReviewCompanies,
      // notifications
      notifs, setNotifs, notifOpen, setNotifOpen,
      // toast & save indicator
      toast, showToast, savingCount, setSavingCount,
      // year pre-selection for Company Master
      companiesYear, setCompaniesYear,
      // persisted Company Master filters/search/sort/view
      companiesUi, setCompaniesUi,
      // lead review context (bridge between LeadsPage and CompanyDetailPage)
      leadReviewCtx, setLeadReviewCtx,
      // pending leads show navigation (set before nav("leads") to pre-select a show+tab)
      pendingLeadsShow, setPendingLeadsShow,
      // pending insights navigation (set before nav("insights") to pre-apply event/show/year filters)
      pendingInsights, setPendingInsights,
      // shared decision modal (single-company Reject / Research / Refer prompts)
      decisionModal, setDecisionModal,
      updateReviewStatus, requestDecision,
      // campaign modal
      campaignModal, setCampaignModal,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
